import { client } from '../client';

// Sentry
import * as Sentry from '@sentry/react';
import i18n from '../../i18n';

// Types
import { OrderType } from '../../types/orders';

//GQL
import {
  GET_IMAGE_BY_ID,
} from '../queries/accidents/accidents';
import { GET_ORDER_BY_ID, GET_SELECTORS_DATA } from '../queries/orders/orders';
import { FETCH_USERS } from '../queries/users';
import {
  GET_CAPITOL_BY_ID,
  GET_SUB_CAPITOL_BY_ID,
  FETCH_CAPITOLS,
  FETCH_SUB_CAPITOLS,
  FETCH_PREDEFINED_ORDERS,
} from '../queries/orders/predefinedData';
import { pkFormatter } from '../../utils/transform';
import { warmupOrdersByIds } from './warmupOrders';

export const selectWorkOrdersWithMultimedia = async (
  dataOrders: any[] = []
) => {
  // Warm-up: mismo patrón que el resto de selectors de tabla (una query
  // `ordersByIds` puebla la caché de Orders antes del per-row). NOTA: hoy
  // las filas de multimedia YA son Orders completas (vienen de
  // FETCH_ORDERS/GET_ORDER_BY_ID en los loaders) y no tienen `orderIDs`,
  // así que esto es un no-op (ids vacíos → return inmediato del helper).
  // Se mantiene por uniformidad y por si el shape vuelve a ser
  // entidad→orderIDs como en las otras tablas. Sin flag reFetch en la firma
  // → cache-first.
  await warmupOrdersByIds(dataOrders.map((o) => o?.orderIDs?.[0]));
  // Read roads from cache (already loaded by the multimedia loader via GET_SELECTORS_DATA)
  let roadsMap: Record<string, string> = {};
  try {
    const selectorsData = client.readQuery({ query: GET_SELECTORS_DATA });
    if (selectorsData?.roads) {
      roadsMap = selectorsData.roads.reduce((acc: Record<string, string>, road: any) => {
        acc[road.id] = road.name;
        return acc;
      }, {});
    }
  } catch {
    // Roads cache not available — roadValue will be empty
  }

  let usersMap: Record<string, string> = {};
  try {
    const usersData = client.readQuery({ query: FETCH_USERS });
    if (usersData?.users) {
      usersMap = usersData.users.reduce((acc: Record<string, string>, u: any) => {
        acc[u.id] = `${u.name} ${u.surName}`.trim();
        return acc;
      }, {});
    }
  } catch {
    // Users cache not available — userValue will be empty
  }

  const ordersWithMultimedia = await Promise.all(
    dataOrders.map(async (order) => {
      // Resiliencia ante datos huérfanos: a diferencia de actions/accidents/
      // communications (que resuelven la Order padre vía GET_ORDER_BY_ID por
      // fila desde un orderIDs[0] potencialmente huérfano), aquí `dataOrders`
      // ya son Orders reales devueltas por FETCH_ORDERS/GET_ORDER_BY_ID en el
      // loader (WorkOrderDetailed ya filtra nulls antes de llamar aquí). Aun
      // así, este guard es defensa en profundidad — si `order` llegara
      // null/undefined, `order.imageIDs` reventaría (TypeError) y el
      // Promise.all rechazaría TODA la tabla por una sola fila corrupta. Se
      // apoya en el `.filter((order) => order !== null)` ya existente abajo.
      if (!order) {
        console.warn(
          '[selectWorkOrdersWithMultimedia] Order recibida null/undefined, fila omitida',
        );
        return null;
      }

      if (order.imageIDs.length > 0) {
        // Use denormalized pk fields directly from Order
        const pkInit = (order.pkInitKm != null && order.pkInitMeter != null)
          ? pkFormatter(order.pkInitKm, order.pkInitMeter)
          : undefined;
        const pkEnd = (order.pkEndKm != null && order.pkEndMeter != null)
          ? pkFormatter(order.pkEndKm, order.pkEndMeter)
          : undefined;

        // Use denormalized roadIDs from Order to look up road name from cache
        const roadId = order.roadIDs?.[0];
        const roadValue = roadId ? roadsMap[roadId] : undefined;
        const userValue = order.userId ? usersMap[order.userId] : undefined;

        let capitolName = '';
        let subCapitolName = '';
        let predefinedOrderName = '';

        // Resolve predefined order name from cache
        try {
          const predefinedData = client.readQuery({ query: FETCH_PREDEFINED_ORDERS });
          if (predefinedData?.predefinedOrders && order.predefinedOrderIDs?.[0]) {
            const found = predefinedData.predefinedOrders.find((p: any) => p.id === order.predefinedOrderIDs[0]);
            predefinedOrderName = found?.name ?? '';
          }
        } catch {
          // Predefined orders cache not available
        }

        // Una Order sin capítulo es normal (tramos, el propio recorrido, una
        // básica a medias): sin id no se pregunta a la red. `capitol(id: ID!)`
        // con un id vacío es un 400, y aquí tumbaba la tabla ENTERA
        // (diagnóstico 2026-10-08 § 9).
        const capitolId = order.capitolIDs?.[0];
        const subCapitolId = order.subCapitolIDs?.[0];

        try {
          const capitolsData = client.readQuery({ query: FETCH_CAPITOLS });
          if (capitolsData?.capitols) {
            const foundCapitol = capitolsData.capitols.find((c: any) => c.id === capitolId);
            capitolName = foundCapitol ? foundCapitol.name : '';
          } else if (capitolId) {
            const { data: { capitol } } = await client.query({ query: GET_CAPITOL_BY_ID, variables: { id: capitolId } });
            capitolName = capitol?.name ?? '';
          }

          const subCapitolsData = client.readQuery({ query: FETCH_SUB_CAPITOLS });
          if (subCapitolsData?.subCapitols) {
            const foundSubCapitol = subCapitolsData.subCapitols.find((c: any) => c.id === subCapitolId);
            subCapitolName = foundSubCapitol ? foundSubCapitol.name : '';
          } else if (subCapitolId) {
            const { data: { subCapitol } } = await client.query({ query: GET_SUB_CAPITOL_BY_ID, variables: { id: subCapitolId } });
            subCapitolName = subCapitol?.name ?? '';
          }
        } catch (e) {
          // fallback to network
          const [capitolResult, subCapitolResult] = await Promise.all([
            capitolId
              ? client.query({ query: GET_CAPITOL_BY_ID, variables: { id: capitolId } })
              : Promise.resolve(null),
            subCapitolId
              ? client.query({ query: GET_SUB_CAPITOL_BY_ID, variables: { id: subCapitolId } })
              : Promise.resolve(null),
          ]);
          capitolName = capitolResult?.data?.capitol?.name ?? '';
          subCapitolName = subCapitolResult?.data?.subCapitol?.name ?? '';
        }

        // Fase 4 nav Trabajo/Revisión (follow-up T4, 2026-07-12) — "Origen"
        // viene directo del campo GraphQL `Order.containerType` (el backend
        // lo denormaliza en Prisma; ver NEW_ORDER fragment). Fallback a
        // `order.type` cuando la propia Order ES el contenedor D0
        // (generic/campaign/surveillanceRoute con imágenes propias) — el
        // backend deja `containerType` null en esos casos (no tienen
        // contenedor). Ya no se deriva por fila con un lookup extra.
        const containerType = order.containerType ?? order.type;

        return {
          id: order.id,
          roid:
            typeof order.roid === 'string'
              ? order.roid.split('.')
              : Array.isArray(order.roid)
                ? order.roid
                : [],
          registerInitDate: order.registerInitDate,
          creationDate: order.creationDate,
          capitol: capitolName,
          subCapitol: subCapitolName,
          // Descripcion EFECTIVA. Esta tabla es de nivel ORDEN y mezcla
          // tipos, asi que hasta 2026-08-04 pintaba `description` a secas y la
          // misma actuacion salia con un texto aqui y otro en su propia tabla.
          // El backend denormaliza la validada en la Order justo para poder
          // resolverlo sin saltar a la hija (en Mongo eso seria un N+1), y su
          // indice de busqueda sigue al mismo texto: lo que se ve se encuentra.
          description: order.descriptionValidated || order.description,
          notes: order.notes,
          imageIDs: order.imageIDs,
          pkInit,
          pkEnd,
          state: order.state,
          road: roadValue,
          user: userValue,
          predefinedOrder: predefinedOrderName,
          containerType,
        };
      }
      return null;
    })
  );

  return ordersWithMultimedia.filter((order) => order !== null);
};

export const selectImagesByOrderIDs = async (idOrder: string) => {
  try {
    const { data } = await client.query({
      query: GET_ORDER_BY_ID,
      variables: {
        id: idOrder,
      },
    });
    const roid = data.order.roid;

    const images = await Promise.all(
      data.order.imageIDs.map(async (imageID: string) => {
        const { data } = await client.query({
          query: GET_IMAGE_BY_ID,
          variables: {
            id: imageID,
          },
        });
        return { ...data.image, roid: roid };
      })
    );

    return {
      images: images,
    };
  } catch (err) {
    Sentry.captureException(err);
    console.log('[multimedia] error obteniendo los datos', err);
  }
};

export const selectImagesByMultipleOrderIDs = async (
  orderIDs: Array<string | undefined>
): Promise<
  Array<{ image: any; roid: string | string[]; orderName: string }>
> => {
  try {
    // Warm-up: UNA query `ordersByIds` puebla la caché con las Orders de las
    // filas seleccionadas; los GET_ORDER_BY_ID per-row de abajo (fetchPolicy
    // default = cache-first) pasan a resolverse de caché (redirect
    // Query.order). Si falla → no-op, per-row intacto como fallback natural.
    await warmupOrdersByIds(orderIDs);

    const allImages = await Promise.all(
      orderIDs.map(async (orderId) => {
        if (!orderId) return [];

        const { data } = await client.query({
          query: GET_ORDER_BY_ID,
          variables: { id: orderId },
        });

        // Un id que no resuelve a Order devuelve `null`, y antes reventaba
        // TODO el `Promise.all` — el catch de abajo se tragaba el lote entero
        // y el informe se quedaba sin NINGUNA imagen por un solo id malo.
        // Pasa de verdad: las filas del listado de Recorridos llevan el id del
        // SurveillanceRoute, no el de su Order gemela.
        const order = data.order;
        if (!order) return [];
        const roid = order.roid;

        const images = await Promise.all(
          order.imageIDs.map(async (imageID: string) => {
            const { data } = await client.query({
              query: GET_IMAGE_BY_ID,
              variables: { id: imageID },
            });
            return {
              image: data.image,
              roid: roid,
              orderName: `${Array.isArray(roid) ? roid.join('.') : roid}`,
            };
          })
        );

        return images;
      })
    );

    return allImages.flat();
  } catch (err) {
    Sentry.captureException(err);
    console.error('[multimedia] error obteniendo los datos', err);
    return [];
  }
};

/**
 * Type for images with source information
 * Indicates where the image comes from (parent order, action, or accident)
 */
export type ImageWithSource = {
  id: string;
  name: string;
  creatingDate: string;
  /** Legacy storage (cloudinary-legacy provider). Null for hetzner-backed images. */
  cloudinaryPublicId?: string | null;
  /** New storage (hetzner provider). Null for legacy images. */
  storageKey?: string | null;
  storageProvider?: string;
  /** Public Hetzner object URL — null for legacy images. The frontend rebuilds
   * the Cloudinary fetch URL from this with the SDK (see utils/cloudinary.ts). */
  publicUrl?: string | null;
  orderIDs: string[];
  roid: string | string[];
  source: {
    type: 'order' | 'action' | 'accident';
    orderId: string;
    orderRoid: string;
  };
};

/**
 * Fetches ALL images for an order including:
 * - Images from the parent order itself
 * - Images from all child Action orders
 * - Images from all child Accident orders
 *
 * @param idOrder - The parent order ID
 * @returns Object with images array and hasChildImages flag
 */
export const selectAllMultimediaByOrderId = async (
  idOrder: string
): Promise<{ images: ImageWithSource[]; hasChildImages: boolean }> => {
  try {
    // 1. Get parent order
    const { data: orderData } = await client.query({
      query: GET_ORDER_BY_ID,
      variables: { id: idOrder },
    });
    const parentOrder = orderData.order;
    const parentRoid = parentOrder.roid;
    const parentRoidString = Array.isArray(parentRoid)
      ? parentRoid.join('.')
      : parentRoid;

    const allImages: ImageWithSource[] = [];

    // 2. Get images from parent order
    if (parentOrder.imageIDs?.length > 0) {
      const parentImages = await Promise.all(
        parentOrder.imageIDs.map(async (imageID: string) => {
          const { data } = await client.query({
            query: GET_IMAGE_BY_ID,
            variables: { id: imageID },
          });
          return {
            ...data.image,
            roid: parentRoid,
            source: {
              type: 'order' as const,
              orderId: idOrder,
              orderRoid: parentRoidString,
            },
          };
        })
      );
      allImages.push(...parentImages);
    }

    // 3. Get images from child orders (Actions and Accidents)
    if (parentOrder.childExpeditionOrderIDs?.length > 0) {
      const childImagesArrays = await Promise.all(
        parentOrder.childExpeditionOrderIDs.map(async (childOrderId: string) => {
          try {
            const { data: childData } = await client.query({
              query: GET_ORDER_BY_ID,
              variables: { id: childOrderId },
            });
            const childOrder = childData.order;

            if (!childOrder.imageIDs || childOrder.imageIDs.length === 0) {
              return [];
            }

            const childRoid = childOrder.roid;
            const childRoidString = Array.isArray(childRoid)
              ? childRoid.join('.')
              : childRoid;

            // Determine source type based on OrderType
            let sourceType: 'action' | 'accident' = 'action';
            if (childOrder.type === OrderType.Accident) {
              sourceType = 'accident';
            }

            const childImages = await Promise.all(
              childOrder.imageIDs.map(async (imageID: string) => {
                const { data } = await client.query({
                  query: GET_IMAGE_BY_ID,
                  variables: { id: imageID },
                });
                return {
                  ...data.image,
                  roid: childRoid,
                  source: {
                    type: sourceType,
                    orderId: childOrderId,
                    orderRoid: childRoidString,
                  },
                };
              })
            );

            return childImages;
          } catch (err) {
            console.error(`Error fetching child order ${childOrderId}:`, err);
            return [];
          }
        })
      );

      // Flatten and add child images
      allImages.push(...childImagesArrays.flat());
    }

    return {
      images: allImages,
      hasChildImages: allImages.some((img) => img.source.type !== 'order'),
    };
  } catch (err) {
    Sentry.captureException(err);
    console.error('[multimedia] error obteniendo los datos', err);
    return { images: [], hasChildImages: false };
  }
};
